import React, { useState, useEffect, useRef } from "react";
import {Box, Collapse, useBreakpointValue, useToast} from "@chakra-ui/react";
import MessageList from "./MessageList";
import InputArea from "./InputArea";
import ChatHistory from "./ChatHistory/ChatHistory";
import CampaignSummary from "./CampaignSummary";
import { streamAPI, cqAPI, getBid, getUid } from "../../lib/Auth";
import {
  checkAttachment, extensionOf, isUploadTurnOnScreen, sanitizeFileName, storedFileName, withoutRecoveredTurn,
} from "./uploadRules";
import { useAppContext } from "../../AppContext";
import {
  createFrameParser, mergeProgress, reviewFinished, panelFromJobCreated,
} from "./streamFrames";
import "./ChatComponent.css";
import _ from "lodash";
import {useLocation, useNavigate} from "react-router-dom";

/**
 * The phrase every placeholder answer the api persists carries — an ordinary question's
 * ("Working on your question — this keeps running if you leave this page…") and a call
 * review's ("Reading all 20 matching calls. This keeps running if you leave this page…").
 * Turns are normally recognised by their chat entry's id (qa_id); this is the fallback for
 * an entry loaded without one, and the test for "is this still a placeholder?".
 */
const PLACEHOLDER_PHRASE = "keeps running if you leave this page";

const TERMINAL_JOB_STATUSES = ["complete", "cancelled", "failed"];

/**
 * The stream protocol this client speaks (sent with every question). 2 = the api also
 * streams the turn's own progress frames — which step the assistant is on, with details —
 * for the activity report; an older client that never declared it gets today's stream.
 */
const STREAM_PROTOCOL = 2;
// How long an attached file's upload may take before it is given up (SS-4267): 10 MB on a
// slow office connection is minutes, and a stalled one must not hold the message box forever.
const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;
// When the answer to an upload was lost (a Stop, a dropped connection), how long to look for
// it in the chat before deciding the server never took it.
const UPLOAD_CHECK_DELAYS_MS = [0, 1000, 2500];
// An answer that is not the api's own (a gateway's 502/504, a server error): the request may
// have been taken all the same, so the chat is asked — it is never treated as a refusal.
const isUnknownOutcome = (response) => !response || !response.status || response.status >= 500;

/** The realtime topic live progress is pushed on: `_po_progress/<bid>/<uid>` (owner-scoped). */
const PROGRESS_TOPIC_BASE = "_po_progress";
/** How long the socket is trusted after an acknowledgement or a frame before HTTP tailing resumes. */
const SOCKET_LEASE_MS = 5 * 60 * 1000;

/**
 * How a finished job whose answer is not on screen yet is asked for again: exponential
 * backoff from `base_ms` capped at `max_ms`, at most `attempts` times PER JOB (one job that
 * never gets its answer must not use up a later one's tries). Exported so tests can shrink
 * the waits.
 */
export const retryPolicy = {
  base_ms: 5000, // re-attach for a finished job whose answer is not on screen
  max_ms: 60000,
  attempts: 6,
  fetch_base_ms: 1500, // a failed fetch of the chat's entries (×1, ×2, ×3)
  attach_fail_base_ms: 2000, // a re-attach the api could not serve (5xx, network)
};

/** A client-side id for one question, so its Stop button stops THIS question and nothing else. */
const newRequestId = () => (
  (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
);

function fixPartialHtml(htmlString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlString, "text/html");

  if (doc.querySelector("parsererror")) {
    const openTags = htmlString.match(/<[^\/>]+>/g) || [];
    const closeTags = htmlString.match(/<\/[^>]+>/g) || [];

    if (openTags.length !== closeTags.length) {
      for (let i = openTags.length - 1; i >= 0; i--) {
        const tag = openTags[i].match(/<(\w+)/)[1];
        if (!htmlString.includes(`</${tag}>`)) {
          htmlString += `</${tag}>`;
        }
      }
    }
  }

  return htmlString;
}

// ── The activity report of a turn ─────────────────────────────────────────────────────
//
// What the assistant is doing for a question, kept on the turn's own message and rendered
// above its answer as a collapsible report (MessageList ActivityReport): the steps it took
// (a tool starting, a tool done with what it found, writing the answer), a call review's
// per-call findings as one of those steps, and whether it is still running.

const emptyActivity = () => ({
  status: null, // 'running' | 'complete' | 'cancelled' | 'failed' | null (nothing recorded)
  steps: [],
  review: null, // the review's progress (streamFrames mergeProgress state), when the turn launched one
  reviewJobId: null,
  reviewAnswered: false,
  startedAt: null,
  endedAt: null,
});

/** The same step arrives as an explanation line, a live frame and a replayed event: compare by wording. */
const normalizeStepLabel = (label) => String(label || "").replace(/[.…\s]+$/g, "").trim().toLowerCase();

/**
 * Append one step, deduplicating what the transports deliver twice (the live stream, the
 * socket and the store; an explanation line and its progress frame), and folding a tool's
 * "working" line into its "done" line so each tool reads as one step, the way a chat
 * assistant's activity log does.
 *
 * The api gives every tool invocation a `step_id` carried by both of its frames: that is
 * what folds them and what keeps two runs of the same tool apart. Frames without one (an
 * explanation line, an older api) fall back to the wording and the tool name.
 */
const hasSeq = (s) => s.seq !== null && s.seq !== undefined;
const appendStep = (activityArg, step) => {
  const activity = activityArg || emptyActivity();
  const steps = [...activity.steps];
  if (hasSeq(step) && steps.some((s) => s.seq === step.seq)) return activity;
  if (step.step_id) {
    const same = steps.findIndex((s) => s.step_id === step.step_id);
    if (same !== -1) {
      const existing = steps[same];
      // A replayed "working" frame of a step that has since finished changes nothing.
      if (existing.stage === "step" && step.stage === "working") return activity;
      steps[same] = { ...existing, ...step, done: step.stage === "working" ? !!existing.done : true, seq: step.seq ?? existing.seq ?? null };
      return { ...activity, steps };
    }
  }
  const key = normalizeStepLabel(step.label);
  const twin = steps.findIndex((s) => s.stage === step.stage && normalizeStepLabel(s.label) === key
    && (!s.step_id || !step.step_id || s.step_id === step.step_id)
    && (!hasSeq(s) || !hasSeq(step)));
  if (twin !== -1) {
    // An explanation-derived line adopts the id and details of the frame that names it.
    steps[twin] = { ...steps[twin], ...step, seq: steps[twin].seq ?? step.seq ?? null };
    return { ...activity, steps };
  }
  if (step.stage === "step" && step.tool) {
    const working = steps.findIndex((s) => s.stage === "working" && s.tool === step.tool && !s.done
      && (!s.step_id || !step.step_id || s.step_id === step.step_id));
    if (working !== -1) {
      steps[working] = { ...steps[working], ...step, done: true };
      return { ...activity, steps };
    }
  }
  steps.push({ ...step, done: step.stage !== "working" && step.stage !== "composing" });
  return { ...activity, steps };
};

/** A job's status never goes back from finished to running (a late replay, a nudge that outran the status write). */
const noDowngrade = (current, next) => (TERMINAL_JOB_STATUSES.includes(current) && !TERMINAL_JOB_STATUSES.includes(next) ? current : next);

/** Every 'working' step becomes done — the turn moved on. */
const settleSteps = (activity) => (activity
  ? { ...activity, steps: activity.steps.map((s) => (s.done ? s : { ...s, done: true })) }
  : activity);

/**
 * The explanation lines the api streams inside the answer ("Looking into your practice
 * database...") are steps, not answer text: taken out of the content and into the report.
 * The regex runs over the JOINED stream so a line split across two reads still matches.
 */
const EXPLANATION_SPAN = /<span class="po-explanation">([\s\S]*?)<\/span>/g;
const extractExplanations = (html) => {
  const labels = [];
  const text = String(html || "").replace(EXPLANATION_SPAN, (m, label) => {
    const clean = String(label || "").replace(/<[^>]+>/g, "").trim();
    if (clean) labels.push(clean);
    return "";
  });
  return { text, labels };
};

/** Is this entry still showing a placeholder rather than an answer? */
/**
 * Is this entry showing the api's "the connection to the assistant was lost" verdict? A
 * complete answer can still land after it (the api lets a late real answer supersede the
 * verdict), so such an entry is re-fetched when its turn is reported finished.
 */
const LOST_PHRASE = "the connection to the assistant was lost";
const showsLostVerdict = (message) => !!message && typeof message.content === "string" && message.content.includes(LOST_PHRASE);

const showsPlaceholder = (message) => !!message && typeof message.content === "string"
  && message.content.includes(PLACEHOLDER_PHRASE) && !message.reviewAnswerForJob;

function ChatComponent({
                         chatContext,
                         setChatContext,
                         chatSessionId,
                         setChatSessionId,
                         selectedBusiness,
                       }) {
  const [messages, setMessages] = useState([]);
  const [questionText, setQuestionText] = useState("");
  const [promptSuggestions, setPromptSuggestions] = useState([]);
  const [longProcessing, setLongProcessing] = useState(false);
  const [disableSendButton, setDisableSendButton] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [newSessionCreated, setNewSessionCreated] = useState(false);
  const abortControllerRef = useRef(null);
  const prevDependencyRef = useRef(null);
  const newButUnusedSessions = useRef({});
  const [chatHistory, setChatHistory] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true); // Add this line
  const isMobile = useBreakpointValue({ base: true, md: false });
  // the file attached with the paperclip and not yet sent (SS-4267): {file, name}
  const [attachment, setAttachment] = useState(null);
  // a picked file is being checked (read and counted): nothing can be sent meanwhile
  const [attachmentChecking, setAttachmentChecking] = useState(false);
  // which pick a check belongs to: a check that finishes after another pick (or after the
  // office moved to another chat) is dropped
  const attachCheckRef = useRef(0);
  // the attached file is uploading: Stop cancels it (uploadAbortRef), its chip says so
  const [uploading, setUploading] = useState(false);
  const uploadAbortRef = useRef(null);
  const toast = useToast();
  const [campaignIds, setCampaignIds] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [showCampaignSummary, setShowCampaignSummary] = useState(false);
  // The app's realtime socket (AppContext): live progress of this user's questions is
  // pushed on it, so nothing has to poll while it is up.
  const { realtime, realtimeInitialized } = useAppContext() || {};
  // Every office optimizer question outlives the request that asked it (SS-4110): an
  // ordinary one keeps running in the api, a call review in worker. The chat keeps track
  // of the jobs it is attached to so it can re-attach after any navigation.
  //
  // Status, kind, "the server says answered" and the chat entry of every job this chat
  // knows about, by job id.
  const jobStatusRef = useRef({});
  const jobKindRef = useRef({});
  const jobAnsweredRef = useRef({});
  const jobQaRef = useRef({});
  // Re-attaches the api could not serve in a row (5xx, network): retried with backoff.
  const attachFailuresRef = useRef(0);
  // A finished job whose answer is not on screen is asked for again, with backoff: how
  // many times each has been, by job id (see retryPolicy).
  const answerRetryRef = useRef({ attempts: {} });
  // Fetches of a single entry (refreshQaEntry) that failed, by entry — retried with backoff.
  const refreshRetriesRef = useRef({});
  // Frames of a review whose turn is not on screen yet (a second tab asked it, or the
  // socket outran the launch), held until the turn is.
  const pendingFramesRef = useRef({});
  const refreshingTurnsRef = useRef(new Set());
  // The review the CURRENT launch stream created, if any.
  const reviewJobIdRef = useRef(null);
  // Whether the next server text of the launch stream is a review's placeholder answer.
  const placeholderPendingRef = useRef(false);
  const attachInFlightRef = useRef(null);
  // The reader of the attach in flight, so a newer attach (or leaving) can cut it off.
  const attachAbortRef = useRef(null);
  // A generation per attach, so an old reader for a session the user left and came back
  // to cannot be mistaken for the current one.
  const attachGenerationRef = useRef(0);
  const reattachTimerRef = useRef(null);
  // The client's own id for the question in flight: sent with the question, and with the
  // Stop button's request so the api stops THIS question and nothing else in the chat.
  const currentRequestIdRef = useRef(null);
  // Why the in-flight request was aborted: "user" (the Stop button) or "navigation"
  // (leaving the chat — the question keeps running on the server, only the reading stops).
  const abortReasonRef = useRef(null);
  // Until when the realtime socket is known to be delivering progress: set when the server
  // acknowledges the subscription and extended by every frame, never assumed. While it
  // holds, a re-attach only replays; once it lapses, the client tails over HTTP again and
  // re-subscribes (a reconnect drops the socket's subscriptions).
  const socketLiveUntilRef = useRef(0);
  const socketLive = () => Date.now() < socketLiveUntilRef.current;
  const subscribeProgressRef = useRef(null);
  // Where the durable replay of each job stands: the highest event seq delivered IN ORDER
  // — by an HTTP replay, or by socket frames that followed on from it without a gap. It is
  // what the next re-attach asks to continue from, and never a seq that arrived out of
  // order: a missed event between two socket frames would otherwise never be replayed.
  const replayCursorRef = useRef({});
  // The turn the CURRENT launch stream answers into, named by the stream's first frame.
  const launchTurnJobRef = useRef(null);
  // A re-check of frames held for the turn its launch stream has not named yet.
  const heldRetryTimerRef = useRef(null);
  // Failed fetches of the chat's entries, by entry — retried a few times with backoff.
  const fetchRetriesRef = useRef({});
  // "A replay is owed": more frames arrived for a turn not yet on screen than could be
  // held, so some were let go. Counted, not a flag: every overflow is one more debt
  // (`owed`), and a re-attach settles only the debt that existed when it STARTED (`settled`)
  // — an overflow during a running replay stays owed and gets the next one. The store has
  // every event and held frames never move the replay cursor, so a replay brings them all.
  const replayDebtRef = useRef({ owed: 0, settled: 0 });
  const replayScheduledRef = useRef(false);
  const replayOwed = () => replayDebtRef.current.owed > replayDebtRef.current.settled;
  // attachSession is defined further down; the hold-and-fetch path reaches it through this.
  const attachSessionRef = useRef(null);
  // The router's CURRENT pathname and session, for callbacks that resolve after a navigation.
  const currentPathnameRef = useRef(location.pathname);
  currentPathnameRef.current = location.pathname;
  const chatSessionIdRef = useRef(chatSessionId);
  chatSessionIdRef.current = chatSessionId;
  // The session this chat is SHOWING: the prop, or the one the route named while the
  // parent has not caught up with it yet.
  const activeSessionIdRef = useRef(chatSessionId);
  if (chatSessionId) activeSessionIdRef.current = chatSessionId;
  // The message list as of the last render, for callbacks that need to read it. Code that
  // replaces the list and then acts on it in the same tick updates it eagerly too.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  // applyStructuredFrame and holdFrameForMissingTurn call each other; the later one is
  // reached through this ref.
  const applyStructuredFrameRef = useRef(null);

  /**
   * Stop applying anything from an attach that belongs to a chat the office has left.
   * Leaving does not always start another attach (a brand-new chat starts none), and the
   * old reader would otherwise keep writing its progress — and its answer — into it.
   */
  const invalidateReviewAttach = () => {
    attachGenerationRef.current += 1;
    attachInFlightRef.current = null;
    reviewJobIdRef.current = null;
    placeholderPendingRef.current = false;
    jobStatusRef.current = {};
    jobKindRef.current = {};
    jobAnsweredRef.current = {};
    jobQaRef.current = {};
    attachFailuresRef.current = 0;
    answerRetryRef.current = { attempts: {} };
    refreshRetriesRef.current = {};
    pendingFramesRef.current = {};
    refreshingTurnsRef.current = new Set();
    replayCursorRef.current = {};
    replayDebtRef.current = { owed: 0, settled: 0 };
    fetchRetriesRef.current = {};
    if (attachAbortRef.current) {
      try { attachAbortRef.current.abort(); } catch (e) { /* already done */ }
      attachAbortRef.current = null;
    }
    if (reattachTimerRef.current) {
      clearTimeout(reattachTimerRef.current);
      reattachTimerRef.current = null;
    }
    if (heldRetryTimerRef.current) {
      clearTimeout(heldRetryTimerRef.current);
      heldRetryTimerRef.current = null;
    }
  };

  /**
   * Record that a job's event was delivered. An HTTP replay is in order by construction; a
   * socket frame counts only when it follows straight on from the cursor, so a gap (a frame
   * lost in flight) leaves the cursor behind it and the next re-attach replays what was
   * missed. Nothing else moves the cursor — not what is on screen, which may have arrived
   * out of order.
   */
  const noteReplayed = (jobId, seq, source) => {
    if (!jobId || typeof seq !== "number") return;
    const cursor = replayCursorRef.current[jobId] || 0;
    if (source === "attach") {
      if (seq > cursor) replayCursorRef.current[jobId] = seq;
    } else if (source === "socket" && seq === cursor + 1) {
      replayCursorRef.current[jobId] = seq;
    }
  };

  /**
   * Leaving the chat: stop READING the answer, without stopping the question. The api no
   * longer takes a closed connection for a Stop (SS-4110), so this is safe — and it keeps
   * a stream started in one chat from painting into the next one.
   */
  const abortForNavigation = () => {
    if (abortControllerRef.current) {
      abortReasonRef.current = "navigation";
      abortControllerRef.current.abort();
    }
  };

  const getNewChatSession = () => {
    return cqAPI
      .post("/session", {
        type: chatContext.type,
        ...(chatContext.for ? { for: chatContext.for } : {}),
      })
      .then((res) => res.data);
  };

  useEffect(() => {
    setLongProcessing(false);
    const newSession = async (sessionIndex) => {
      setDisableSendButton(true);
      newButUnusedSessions.current[sessionIndex] = {
        loading: true,
        started: Date.now(),
      };
      const session = await getNewChatSession();

      if (newButUnusedSessions.current[sessionIndex].loading) {
        newButUnusedSessions.current[sessionIndex] = session;
      }

      if (prevDependencyRef.current && prevDependencyRef.current.type) {
        const currentSessionIndex =
          `${prevDependencyRef.current.type}${
            selectedBusiness && selectedBusiness.bid
          }` +
          (prevDependencyRef.current.for
            ? `:${prevDependencyRef.current.for}`
            : "");
        if (currentSessionIndex === sessionIndex && session) {
          setChatSessionId(session.session_id);
          setPromptSuggestions(session.prompt_suggestions);
          setDisableSendButton(false);
        }
      }
    };

    if (chatContext.type && !_.isEqual(prevDependencyRef.current, chatContext)) {
      setQuestionText("");
      if (!chatContext.history) {
        const sessionIndex =
          `${chatContext.type}${selectedBusiness && selectedBusiness.bid}` +
          (chatContext.for ? `:${chatContext.for}` : "");
        setPromptSuggestions([]);
        setMessages([]);
        // A different chat: whatever a previous chat was streaming stops here (the work
        // itself carries on server-side).
        abortForNavigation();
        invalidateReviewAttach();
        if (newButUnusedSessions.current[sessionIndex]) {
          const session = newButUnusedSessions.current[sessionIndex];
          if (session.loading) {
            if (session.started && Date.now() - session.started < 20000) {
              setDisableSendButton(true);
              return;
            }
            newButUnusedSessions.current[sessionIndex] = undefined;
            newSession(sessionIndex);
            return;
          }
          setChatSessionId(session.session_id);
          setPromptSuggestions(session.prompt_suggestions);
          setDisableSendButton(false);
        } else {
          newSession(sessionIndex);
        }
      } else {
        setDisableSendButton(false);
        setPromptSuggestions([]);
      }
    } else {
      setPromptSuggestions([]);
      setDisableSendButton(false);
    }
    prevDependencyRef.current = chatContext;
  }, [chatContext, selectedBusiness]);

  const currentServerChunks = useRef([]);
  const chatBoxRef = useRef(null);
  const longProcessingTimerRef = useRef(null);

  const getSession = async (sessionId) => cqAPI
    .get(`/sessions?session_id=${sessionId}`);

  const sameId = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined && String(a) === String(b);

  /**
   * The message of a turn, wherever it sits: by the request id of the question in flight,
   * by its chat entry's id, by the review it launched, or — for an entry loaded without an
   * id — by the placeholder it still shows.
   */
  const findTurnIndex = (list, { turnKey = null, qaId = null, jobId = null, byPlaceholder = false } = {}) => {
    let index = -1;
    if (turnKey) index = list.findIndex((m) => m.turnKey === turnKey && (m.type === "server" || m.type === "loading"));
    if (index === -1 && qaId) index = list.findIndex((m) => (m.type === "server" || m.type === "loading") && sameId(m.qa_id, qaId));
    if (index === -1 && jobId) index = list.findIndex((m) => m.type === "server" && (m.activity?.reviewJobId === jobId || m.reviewPlaceholderForJob === jobId || m.reviewAnswerForJob === jobId));
    if (index === -1 && byPlaceholder) {
      for (let i = list.length - 1; i >= 0; i--) {
        if (showsPlaceholder(list[i]) && !list[i].activity?.reviewJobId) { index = i; break; }
      }
    }
    return index;
  };

  /**
   * A finished turn's entry, fetched when this chat only has its placeholder. The entry
   * counts as answered only once it holds its FINAL text — not a placeholder, and not the
   * lost verdict on a turn that turned out to finish after all; until then the turn stays
   * pending (its report keeps working) and the re-attach loop asks for it again. With
   * `keepPendingIfPlaceholder` off (no job exists for it any more) a placeholder is left as
   * it is.
   */
  const refreshQaEntry = async (qaId, sessionId = activeSessionIdRef.current, { keepPendingIfPlaceholder = true } = {}) => {
    if (!sessionId || !qaId) return;
    try {
      const data = await getSession(sessionId);
      delete refreshRetriesRef.current[String(qaId)];
      if (activeSessionIdRef.current !== sessionId) return;
      const entries = data?.data?.[0]?.qa ?? [];
      const entry = entries.find((e) => sameId(e._id, qaId));
      if (!entry) return;
      const owner = (messagesRef.current || []).find((m) => m.type === "server" && sameId(m.qa_id, qaId));
      const turnJob = owner?.activity?.turnJobId || null;
      const turnStatus = turnJob ? jobStatusRef.current[turnJob] : null;
      const text = typeof entry.r_text === "string" ? entry.r_text : "";
      const final = !!text && !text.includes(PLACEHOLDER_PHRASE)
        && !(text.includes(LOST_PHRASE) && !!turnStatus && turnStatus !== "failed");
      if (final) {
        // The entry holds its answer now: the jobs it belongs to are answered.
        const reviewJob = owner?.activity?.reviewJobId;
        if (reviewJob) jobAnsweredRef.current[reviewJob] = true;
        if (turnJob) jobAnsweredRef.current[turnJob] = true;
      } else if (!keepPendingIfPlaceholder) {
        return;
      }
      setMessages((prev) => prev.map((m) => {
        if (!(m.type === "server" && sameId(m.qa_id, qaId))) return m;
        if (!final) return { ...m, pending: true };
        return {
          ...m,
          content: text,
          r_buffers: entry.r_buffers ?? m.r_buffers ?? [],
          pending: false,
          activity: m.activity ? settleSteps({ ...m.activity, status: TERMINAL_JOB_STATUSES.includes(m.activity.status) ? m.activity.status : "complete", endedAt: m.activity.endedAt || Date.now() }) : m.activity,
        };
      }));
    } catch (error) {
      console.error("Error refreshing the answer:", error);
      // The entry stays as it is (pending, if it was): try again in a moment, a few times.
      // Beyond that, the re-attach loop keeps asking while the answer is not on screen.
      const attempts = (refreshRetriesRef.current[String(qaId)] || 0) + 1;
      refreshRetriesRef.current[String(qaId)] = attempts;
      if (attempts <= 3) {
        setTimeout(() => {
          if (activeSessionIdRef.current === sessionId) refreshQaEntry(qaId, sessionId, { keepPendingIfPlaceholder });
        }, retryPolicy.fetch_base_ms * attempts);
      } else {
        delete refreshRetriesRef.current[String(qaId)];
      }
    }
  };

  /**
   * Does this entry still need its answer fetched? Yes while it is pending or shows a
   * placeholder — and while it shows the lost verdict of a turn that is NOT definitively
   * failed (a complete answer can supersede the verdict). The verdict of a failed turn is
   * its final text.
   */
  const needsRefresh = (message, jobStatus = null) => !!message && (
    !!message.pending || showsPlaceholder(message) || (showsLostVerdict(message) && jobStatus !== "failed")
  );

  /**
   * Is this job's answer on screen? Decided from the message of the turn it belongs to —
   * found by the job, or by the entry the job was announced with: one still showing a
   * placeholder (or pending) is not answered as far as the office is concerned, whatever
   * the server has persisted. A job whose entry is known but not on screen at all is not
   * displayed either. Only a job with no known entry falls back to the server's word.
   */
  const answerDisplayed = (jobId) => {
    const status = jobStatusRef.current[jobId] || null;
    const messages = messagesRef.current || [];
    const byJob = messages.find((m) => m.type === "server"
      && (m.activity?.turnJobId === jobId || m.activity?.reviewJobId === jobId || m.reviewAnswerForJob === jobId));
    if (byJob) return !needsRefresh(byJob, status);
    const qaId = jobQaRef.current[jobId] || null;
    if (qaId) {
      const byEntry = messages.find((m) => m.type === "server" && sameId(m.qa_id, qaId));
      return byEntry ? !needsRefresh(byEntry, status) : false;
    }
    return !!jobAnsweredRef.current[jobId];
  };

  /**
   * A frame for a turn this chat does not show yet — a review asked from another tab, or a
   * socket nudge that outran the launch. Its frames are held, the chat's entries are
   * fetched and the missing turns appended (in order), and the held frames re-applied.
   */
  /** A replay is owed (see replayDebtRef): re-attach, once, on the next tick. */
  const requestReplay = (sessionId) => {
    if (!replayOwed() || replayScheduledRef.current) return;
    replayScheduledRef.current = true;
    setTimeout(() => {
      replayScheduledRef.current = false;
      if (activeSessionIdRef.current === sessionId && replayOwed()) attachSessionRef.current?.(sessionId);
    }, 0);
  };

  const releaseHeldFrames = (jobId, sessionId) => {
    const held = pendingFramesRef.current[jobId];
    delete pendingFramesRef.current[jobId];
    for (const { frame, source } of held?.frames || []) applyStructuredFrameRef.current?.(frame, { job_id: jobId, source, sessionId });
    requestReplay(sessionId);
  };

  /**
   * The chat's entries were fetched for this entry: every job waiting for it is released —
   * whether the fetch appended the entry or found it already on screen — and, if the
   * server has no such entry, their frames are dropped (there is nothing to show them in).
   */
  const settleHeldFramesFor = (qaId, sessionId, { present }) => {
    for (const [jobId, held] of Object.entries(pendingFramesRef.current)) {
      if (!sameId(held.qaId, qaId)) continue;
      if (present) releaseHeldFrames(jobId, sessionId);
      else delete pendingFramesRef.current[jobId];
    }
  };

  /**
   * Show the chat entries not on screen yet (their question, its file and the answer so far).
   * An upload turn shown live before its entry id arrived is on screen too (SS-4267).
   */
  const appendMissingEntries = (entries) => {
    const shown = new Set((messagesRef.current || []).filter((m) => m.qa_id).map((m) => String(m.qa_id)));
    const missing = (entries || []).filter((e) => e._id && !shown.has(String(e._id))
      && !isUploadTurnOnScreen(e, messagesRef.current));
    if (!missing.length) return;
    const appended = missing.flatMap((e) => [
      { type: "user", content: e.q_text, q_buffers: e.q_buffers ?? [], qa_id: String(e._id) },
      { type: "server", content: e.r_text, r_buffers: e.r_buffers ?? [], qa_id: String(e._id) },
    ]);
    messagesRef.current = [...(messagesRef.current || []), ...appended];
    setMessages((prev) => [...prev, ...appended]);
  };

  /**
   * Fetch the chat's entries so a turn this tab has not seen can be shown, then release
   * whatever waits for it. A fetch that fails is retried a few times, with backoff; after
   * that the held frames are let go (the next re-attach replays the store anyway).
   */
  const fetchMissingTurn = (qaId, sessionId) => {
    // One fetch per entry, however many jobs are waiting for it.
    if (refreshingTurnsRef.current.has(String(qaId))) return;
    refreshingTurnsRef.current.add(String(qaId));
    const waitingFor = (id) => Object.values(pendingFramesRef.current).some((h) => sameId(h.qaId, id));
    getSession(sessionId).then((data) => {
      delete fetchRetriesRef.current[String(qaId)];
      if (activeSessionIdRef.current !== sessionId) return;
      const entries = data?.data?.[0]?.qa ?? [];
      const shown = new Set((messagesRef.current || []).filter((m) => m.qa_id).map((m) => String(m.qa_id)));
      appendMissingEntries(entries);
      // The turn is on screen now (appended, or there all along): what was held for it
      // applies — and a replay follows if more arrived than could be held (releaseHeldFrames).
      const present = shown.has(String(qaId)) || entries.some((e) => sameId(e._id, qaId));
      setTimeout(() => settleHeldFramesFor(qaId, sessionId, { present }), 0);
    }).catch((error) => {
      console.error("Error fetching the chat's newer turns:", error);
      const attempts = (fetchRetriesRef.current[String(qaId)] || 0) + 1;
      fetchRetriesRef.current[String(qaId)] = attempts;
      if (attempts <= 3) {
        setTimeout(() => {
          if (activeSessionIdRef.current === sessionId && waitingFor(qaId)) fetchMissingTurn(qaId, sessionId);
        }, retryPolicy.fetch_base_ms * attempts);
      } else {
        delete fetchRetriesRef.current[String(qaId)];
        settleHeldFramesFor(qaId, sessionId, { present: false });
      }
    }).finally(() => {
      refreshingTurnsRef.current.delete(String(qaId));
    });
  };

  const HELD_FRAMES_MAX = 50;
  const holdFrameForMissingTurn = (frame, { jobId, qaId, sessionId, source = "attach" }) => {
    if (!jobId) return;
    const held = pendingFramesRef.current[jobId] || { qaId: qaId || null, frames: [] };
    held.qaId = held.qaId || qaId || null;
    if (held.frames.length < HELD_FRAMES_MAX) {
      held.frames.push({ frame, source });
    } else {
      // Full. The store has every event and a replay is now owed — durable state, not a
      // property of this queue, which a release throws away (releaseHeldFrames asks for
      // it); a TERMINAL frame is kept in place of the oldest, so the turn is never left
      // spinning until that replay.
      replayDebtRef.current.owed += 1;
      if (frame.type === "progress" && reviewFinished(frame.content)) {
        held.frames.shift();
        held.frames.push({ frame, source });
      }
    }
    pendingFramesRef.current[jobId] = held;
    if (!qaId || !sessionId) return;
    // The question this chat is asking RIGHT NOW has no entry id yet — its launch stream
    // names it in its first frame. A review it launched, whose first frame arrives over
    // the socket before that, waits for the name: fetching the entry now would append a
    // second copy of the question the office is watching being answered.
    const launching = currentRequestIdRef.current
      && (messagesRef.current || []).some((m) => m.turnKey === currentRequestIdRef.current && !m.qa_id);
    if (launching) {
      if (!heldRetryTimerRef.current) {
        heldRetryTimerRef.current = setTimeout(() => {
          heldRetryTimerRef.current = null;
          if (activeSessionIdRef.current === sessionId) {
            for (const heldJob of Object.keys(pendingFramesRef.current)) releaseHeldFrames(heldJob, sessionId);
          }
        }, 1000);
      }
      return;
    }
    fetchMissingTurn(qaId, sessionId);
  };

  /**
   * Apply one structured frame to the message list.
   *
   * `source` says which stream carried it: the LAUNCH of a question (POST /answer), a
   * re-ATTACH to the chat (POST /answer/review), or the realtime SOCKET. Progress lands on
   * the turn's own message as its activity report; a call review's frames are merged by
   * seq, so a duplicate delivery (the socket and a replay) is a no-op.
   */
  const applyStructuredFrame = (parsed, {
    job_id: streamJobId = null, source = "launch", sessionId = activeSessionIdRef.current,
  } = {}) => {
    if (parsed.type === "done" || parsed.type === "answer") return; // markers, nothing to render
    const content = parsed.content || {};
    const live = source === "launch";
    const turnKey = live ? currentRequestIdRef.current : null;
    // Which entry a job answers into, whenever a frame says: what "is its answer on
    // screen?" is judged by when the job's own turn cannot be found (answerDisplayed).
    if (content.job_id && content.qa_id) jobQaRef.current[content.job_id] = String(content.qa_id);

    if (parsed.type === "job_created") {
      const jobId = content.job_id || null;
      const kind = content.kind || "review";
      const status = noDowngrade(jobId ? jobStatusRef.current[jobId] : null, content.status || (kind === "turn" ? "running" : "queued"));
      if (jobId) {
        jobStatusRef.current[jobId] = status;
        jobKindRef.current[jobId] = kind;
        if (content.answered) jobAnsweredRef.current[jobId] = true;
      }

      if (kind === "turn") {
        // An ordinary question of this chat: the one being asked right now (the launch
        // stream names it — and its chat entry — in its first frame), one still being
        // answered on the server, or one just finished, whose report shows it is being
        // worked on until the answer is fetched.
        if (live) launchTurnJobRef.current = jobId || launchTurnJobRef.current;
        const finished = TERMINAL_JOB_STATUSES.includes(status);
        // Decided from the list as last rendered: an updater runs later, when React gets
        // round to it, so nothing decided inside one can be read after the call.
        const shown = messagesRef.current || [];
        const shownIndex = findTurnIndex(shown, { turnKey, qaId: content.qa_id, byPlaceholder: !content.qa_id });
        if (shownIndex === -1 && !live && content.qa_id) {
          // A question asked from another tab: its turn is fetched, then this applies.
          holdFrameForMissingTurn(parsed, { jobId, qaId: content.qa_id, sessionId, source });
          return;
        }
        const refresh = finished && shownIndex !== -1 && needsRefresh(shown[shownIndex], status);
        // Answered: the api says so, or the answer is already on screen. A finished turn
        // whose entry still shows a placeholder is NOT — its report keeps working and the
        // re-attach loop asks for it again until the answer lands.
        if (jobId && finished && (content.answered || (shownIndex !== -1 && !needsRefresh(shown[shownIndex], status)))) jobAnsweredRef.current[jobId] = true;
        setMessages((prev) => {
          const list = [...prev];
          const index = findTurnIndex(list, { turnKey, qaId: content.qa_id, byPlaceholder: !content.qa_id });
          if (index === -1) return prev;
          const m = list[index];
          const activity = {
            ...(m.activity || emptyActivity()),
            status: finished ? status : noDowngrade(m.activity?.status, "running"),
            turnJobId: jobId || m.activity?.turnJobId || null,
          };
          if (finished) activity.endedAt = activity.endedAt || Date.now();
          // "Answered" server-side is not "on screen": a placeholder stays pending until the
          // fetched entry replaces it (refreshQaEntry), however the api describes the job.
          let pending = true;
          if (live) pending = m.pending;
          else if (finished) pending = needsRefresh(m, status);
          list[index] = {
            ...m,
            type: m.type === "loading" ? "loading" : "server",
            content: m.content || (m.type === "loading" ? null : ""),
            qa_id: m.qa_id || content.qa_id || null,
            pending,
            activity: finished ? settleSteps(activity) : activity,
          };
          return list;
        });
        if (refresh && content.qa_id) refreshQaEntry(content.qa_id, sessionId);
        return;
      }

      // A call review: the turn that launched it gets the review as a step of its report.
      if (live) {
        reviewJobIdRef.current = jobId || reviewJobIdRef.current;
        // The next server text of this turn is the placeholder answer ("Reading all 20
        // matching calls…"), which the composed answer replaces when it eventually arrives.
        placeholderPendingRef.current = true;
      }
      const shown = messagesRef.current || [];
      const shownIndex = findTurnIndex(shown, { turnKey, qaId: content.qa_id, jobId, byPlaceholder: !content.answered });
      const refresh = !!content.answered && shownIndex !== -1 && needsRefresh(shown[shownIndex]);
      if (shownIndex === -1 && !live) {
        if (content.qa_id) {
          // Its turn is not on screen (asked from another tab, or the nudge outran the
          // launch): hold the frame, fetch the missing turn, then apply.
          holdFrameForMissingTurn(parsed, { jobId, qaId: content.qa_id, sessionId, source });
          return;
        }
      }
      setMessages((prev) => {
        const list = [...prev];
        const index = findTurnIndex(list, { turnKey, qaId: content.qa_id, jobId, byPlaceholder: !content.answered });
        if (index === -1) {
          // A review whose turn cannot be identified at all (an entry loaded without its id
          // by an older api): shown on its own, under the chat.
          list.push({ type: "progress", content: panelFromJobCreated({ ...content, status }) });
          return list;
        }
        const m = list[index];
        const activity = { ...(m.activity || emptyActivity()) };
        activity.reviewJobId = jobId || activity.reviewJobId;
        activity.startedAt = activity.startedAt || Date.now();
        if (activity.review && activity.review.job_id === jobId) {
          const nextStatus = noDowngrade(activity.review.job_status, status);
          if (nextStatus !== activity.review.job_status) activity.review = { ...activity.review, job_status: nextStatus };
        } else {
          activity.review = panelFromJobCreated({ ...content, status });
        }
        const serverAnswered = (m.reviewAnswerForJob && m.reviewAnswerForJob === jobId) || !!content.answered;
        // Answered server-side is not "on screen": the report completes only once the
        // answer — composed here, or fetched from the entry — has replaced the placeholder;
        // until then it keeps working (and the entry is fetched, again if need be).
        const shownFinal = serverAnswered && !needsRefresh(m, status);
        if (serverAnswered) activity.reviewAnswered = true;
        if (shownFinal) {
          activity.status = "complete";
          activity.endedAt = activity.endedAt || Date.now();
        } else if (!TERMINAL_JOB_STATUSES.includes(activity.status)) {
          activity.status = "running";
        }
        list[index] = {
          ...m,
          type: m.type === "loading" ? "loading" : "server",
          qa_id: m.qa_id || content.qa_id || null,
          ...(!serverAnswered ? { isReviewPlaceholder: true, reviewPlaceholderForJob: jobId || m.reviewPlaceholderForJob || null } : {}),
          pending: !shownFinal,
          activity: shownFinal ? settleSteps(activity) : activity,
        };
        return list;
      });
      if (refresh && content.qa_id) refreshQaEntry(content.qa_id, sessionId);
      return;
    }

    if (parsed.type === "progress") {
      const kind = content.kind || "review";
      // The launch stream names a review on its job_created, not on every event, so a
      // review's event inherits the review whose stream carried it; a turn's step is the
      // launch's own turn.
      const jobId = content.job_id
        || (kind === "review" ? (streamJobId || reviewJobIdRef.current) : (live ? launchTurnJobRef.current : null))
        || null;
      if (content.job_status && jobId) jobStatusRef.current[jobId] = noDowngrade(jobStatusRef.current[jobId], content.job_status);
      if (jobId && !jobKindRef.current[jobId]) jobKindRef.current[jobId] = kind;

      if (kind === "turn") {
        // A step of the assistant for a question: appended to the turn's report. Its
        // terminal step means the answer is in the entry — fetch it if only the
        // placeholder is on screen.
        const finished = reviewFinished(content);
        const shown = messagesRef.current || [];
        const shownIndex = findTurnIndex(shown, { turnKey: live ? turnKey : null, qaId: content.qa_id });
        if (shownIndex === -1 && !live && content.qa_id) {
          // A step of a question asked from another tab: held until its turn is on screen.
          holdFrameForMissingTurn(parsed, { jobId, qaId: content.qa_id, sessionId, source });
          return;
        }
        const refresh = finished && shownIndex !== -1 && needsRefresh(shown[shownIndex], content.job_status);
        // Counted for the replay cursor only now that it is being applied to a turn.
        if (!live && shownIndex !== -1) noteReplayed(jobId, content.seq, source);
        // Over with its answer on screen: answered. Over with only a placeholder on screen:
        // not yet — the entry is fetched, and asked for again if it is still a placeholder.
        if (jobId && finished && shownIndex !== -1 && !needsRefresh(shown[shownIndex], content.job_status)) jobAnsweredRef.current[jobId] = true;
        setMessages((prev) => {
          const list = [...prev];
          const index = findTurnIndex(list, { turnKey: live ? turnKey : null, qaId: content.qa_id });
          if (index === -1) return prev;
          const m = list[index];
          let activity = m.activity || emptyActivity();
          if (jobId && !activity.turnJobId) activity = { ...activity, turnJobId: jobId };
          if (content.stage && content.stage !== "done") {
            activity = appendStep(activity, {
              seq: content.seq ?? null, step_id: content.step_id || null, stage: content.stage, label: content.label || "",
              detail: content.detail || null, tool: content.tool || null, ok: content.ok, elapsed_ms: content.elapsed_ms, at: content.at || null,
            });
          }
          if (content.stage === "frame") return prev;
          if (finished) {
            activity = settleSteps({ ...activity, status: content.job_status, endedAt: activity.endedAt || Date.now() });
          } else if (!TERMINAL_JOB_STATUSES.includes(activity.status)) {
            activity = { ...activity, status: "running", startedAt: activity.startedAt || Date.now() };
          }
          // Finished with only a placeholder on screen (a returning client): pending until
          // the fetched entry holds the answer.
          const pending = finished ? (!live && needsRefresh(m, content.job_status)) : m.pending;
          list[index] = { ...m, type: m.type === "loading" ? "loading" : "server", content: m.content || "", pending, activity };
          return list;
        });
        if (refresh && content.qa_id && !live) refreshQaEntry(content.qa_id, sessionId);
        return;
      }

      // A batch of a call review: merged into the review step of the turn that launched it.
      const frameContent = { ...content, job_id: jobId };
      if (!live && content.qa_id && findTurnIndex(messagesRef.current || [], { jobId, qaId: content.qa_id }) === -1) {
        holdFrameForMissingTurn(parsed, { jobId, qaId: content.qa_id, sessionId, source });
        return;
      }
      if (!live) noteReplayed(jobId, content.seq, source);
      setMessages((prev) => {
        const list = [...prev];
        const index = findTurnIndex(list, { jobId, qaId: content.qa_id, turnKey: live ? turnKey : null });
        if (index === -1) {
          // Legacy shape: a standalone panel (see job_created above).
          const existing = list.findIndex((m) => m.type === "progress" && (!jobId || !m.content?.job_id || m.content.job_id === jobId));
          if (existing !== -1) {
            const merged = mergeProgress(list[existing].content, frameContent);
            if (merged === list[existing].content) return prev;
            list[existing] = { type: "progress", content: merged };
            return list;
          }
          list.push({ type: "progress", content: mergeProgress(null, frameContent) });
          return list;
        }
        const m = list[index];
        const activity = { ...(m.activity || emptyActivity()) };
        const merged = mergeProgress(activity.review, frameContent);
        if (merged === activity.review) return prev; // already rendered
        activity.review = merged;
        activity.reviewJobId = activity.reviewJobId || jobId;
        activity.startedAt = activity.startedAt || Date.now();
        if (!activity.reviewAnswered && !TERMINAL_JOB_STATUSES.includes(activity.status)) activity.status = "running";
        list[index] = { ...m, type: m.type === "loading" ? "loading" : "server", content: m.content || "", activity };
        return list;
      });
      return;
    }

    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages];
      const lastMsg = updatedMessages[updatedMessages.length - 1];
      // If last message is loading or empty server, replace it
      if (lastMsg && (lastMsg.type === "loading" || (lastMsg.type === "server" && !lastMsg.content))) {
        updatedMessages[updatedMessages.length - 1] = { type: parsed.type, content: parsed.content };
      } else {
        updatedMessages.push({ type: parsed.type, content: parsed.content });
      }
      return updatedMessages;
    });
  };

  applyStructuredFrameRef.current = applyStructuredFrame;
  // (attachSessionRef is assigned right after attachSession, further down.)

  /** Append streamed answer text to the message of the question in flight. */
  const applyStreamedText = (rawText) => {
    // Consumed once: only the FIRST server text after a launch's job_created is the
    // review's placeholder answer.
    const markPlaceholder = placeholderPendingRef.current;
    if (markPlaceholder) placeholderPendingRef.current = false;
    const reviewJobId = reviewJobIdRef.current;
    const turnKey = currentRequestIdRef.current;
    let chunkText = rawText.replace(/(```html|```)/g, "");
    chunkText = fixPartialHtml(chunkText);
    currentServerChunks.current.push(chunkText);
    // Read HERE, not inside the updater: the updater runs when React gets round to it,
    // and a structured frame processed in the same batch clears this buffer (see
    // processReadableStream). The explanation lines the api streams inside the answer are
    // steps of the report, not answer text.
    const { text: streamedContent, labels } = extractExplanations(currentServerChunks.current.join(""));

    setMessages((prevMessages) => {
      const list = [...prevMessages];
      // The turn's own message, wherever it sits. Older shapes (no request id) fall back to
      // the last message.
      let index = findTurnIndex(list, { turnKey });
      if (index === -1) {
        const last = list.length - 1;
        if (last >= 0 && (list[last].type === "loading"
          || (list[last].type === "server" && (!list[last].content || !list[last].qa_id) && !list[last].isReviewPlaceholder))) {
          index = last;
        }
      }
      const base = index !== -1 ? list[index] : {};
      let activity = base.activity || emptyActivity();
      // Each explanation line becomes a step ONCE: the joined stream is re-read on every
      // chunk, and a line already folded into its tool's finished step must not come back
      // as a new "working" step.
      for (const label of labels) {
        const key = normalizeStepLabel(label);
        if ((activity.explanations || []).includes(key)) continue;
        activity = appendStep(activity, { stage: "working", label, seq: null });
        activity = { ...activity, explanations: [...(activity.explanations || []), key] };
      }
      if (streamedContent.trim()) {
        // The answer is coming: whatever was "working" is done, including writing it.
        activity = settleSteps(activity);
      }
      if (!activity.status) activity = { ...activity, status: "running", startedAt: activity.startedAt || Date.now() };
      const next = {
        ...base,
        type: "server",
        content: streamedContent,
        activity,
        ...(turnKey ? { turnKey } : {}),
        ...(markPlaceholder ? { isReviewPlaceholder: true, reviewPlaceholderForJob: reviewJobId || null } : {}),
      };
      if (index !== -1) list[index] = next;
      else list.push(next);
      return list;
    });
  };

  const processReadableStream = async (reader, sessionId) => {
    setLongProcessing(false);
    // Frames are newline-delimited and may arrive several to a read, or split across
    // reads; the parser carries the incomplete tail over (SS-4110).
    const parser = createFrameParser();
    // ONE decoder for the whole stream: a multi-byte character split across two reads
    // would otherwise decode to replacement characters in both halves and corrupt the
    // frame it belongs to.
    const decoder = new TextDecoder();
    let cancelled = false;
    // The review this stream launched, if any: its progress events belong to it and to no
    // other review the chat may already be showing.
    let streamJobId = null;
    const turnKey = currentRequestIdRef.current;
    for (;;) {
      longProcessingTimerRef.current = setTimeout(() => {
        setLongProcessing(true);
      }, 3000);
      const { done, value } = await reader.read();
      clearTimeout(longProcessingTimerRef.current);
      longProcessingTimerRef.current = null;
      setLongProcessing(false);

      if (done) {
        const tail = decoder.decode();
        for (const event of [...parser.push(tail), ...parser.flush()]) {
          if (event.kind === "frame") {
            if (event.frame.type === "job_created" && event.frame.content?.job_id && (event.frame.content.kind || "review") !== "turn") {
              streamJobId = event.frame.content.job_id;
            }
            applyStructuredFrame(event.frame, { job_id: streamJobId, source: "launch", sessionId });
          } else applyStreamedText(event.text);
        }
        const launchedReview = !!reviewJobIdRef.current;
        await getSession(sessionId).then(sessionData => {
          const entries = sessionData?.data ?? [];
          const lastEntry = entries.length ? entries[entries.length - 1] : null;
          const qas = lastEntry?.qa ?? [];
          const lastQa = qas.length ? qas[qas.length - 1] : null;
          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            // The turn's own message; for an older shape, the last server message.
            let index = findTurnIndex(updatedMessages, { turnKey });
            if (index === -1) {
              for (let i = updatedMessages.length - 1; i >= 0; i--) {
                if (updatedMessages[i].type === "server" || updatedMessages[i].type === "loading") {
                  index = i;
                  break;
                }
              }
            }
            if (index === -1) return updatedMessages;
            const msg = updatedMessages[index];
            let activity = msg.activity || null;
            if (activity && !launchedReview && !TERMINAL_JOB_STATUSES.includes(activity.status)) {
              // The answer is in: the report is over. (A review's turn stays "running" until
              // the review's answer arrives through the attach.)
              activity = settleSteps({ ...activity, status: "complete", endedAt: Date.now() });
            }
            updatedMessages[index] = {
              ...msg,
              type: "server",
              content: msg.content || lastQa?.r_text || '',
              r_buffers: lastQa?.r_buffers,
              // The entry this turn owns, so a review's progress and answer land on it.
              qa_id: msg.qa_id || (lastQa?._id ? String(lastQa._id) : null),
              pending: launchedReview ? msg.pending : false,
              activity,
            };
            return updatedMessages;
          });
        }).catch(err => console.error('cannot retrieve session info:', err));
        break;
      }

      const chunkText = decoder.decode(value, { stream: true });
      for (const event of parser.push(chunkText)) {
        if (event.kind === "frame") {
          if (event.frame.type === "cancelled") {
            setMessages((prevMessages) => {
              const updatedMessages = [...prevMessages];
              let index = findTurnIndex(updatedMessages, { turnKey });
              if (index === -1) index = updatedMessages.length - 1;
              if (index >= 0) {
                const m = updatedMessages[index];
                updatedMessages[index] = {
                  ...m,
                  type: "server",
                  content: "Request cancelled.",
                  pending: false,
                  activity: m.activity ? settleSteps({ ...m.activity, status: "cancelled", endedAt: Date.now() }) : m.activity,
                };
              }
              return updatedMessages;
            });
            if (launchTurnJobRef.current) {
              jobStatusRef.current[launchTurnJobRef.current] = "cancelled";
              jobAnsweredRef.current[launchTurnJobRef.current] = true;
            }
            cancelled = true;
            break;
          }
          if (event.frame.type === "job_created" && event.frame.content?.job_id && (event.frame.content.kind || "review") !== "turn") {
            streamJobId = event.frame.content.job_id;
          }
          applyStructuredFrame(event.frame, { job_id: streamJobId, source: "launch", sessionId });
          // Clear text buffer so subsequent chunks don't duplicate prior content
          if (event.frame.type !== "progress") currentServerChunks.current = [];
          continue;
        }
        applyStreamedText(event.text);
      }
      if (cancelled) break;
    }
  };

  /**
   * Re-attach to everything this chat has running or unfinished (SS-4110): every call
   * review (its progress belongs to the chat for good), every question still being
   * answered and every one finished a moment ago. Asked for when a chat is opened, the
   * moment a launch stream that started a review ends, whenever the socket says a review
   * finished, and again while anything is still running: the api replays every event after
   * each job's replay cursor (see noteReplayed), keeps tailing while they run (unless the
   * socket is delivering live progress, when it only replays), and composes a finished
   * review's answer lazily. 404 simply means there is nothing here — the normal case.
   */
  const attachSession = async (sessionId) => {
    if (!sessionId) return;
    // A newer attach supersedes an older one: the old reader is cut off, and the new one
    // stops applying as soon as it is no longer THE attach (session and generation), so
    // navigating between chats — including away and back — cannot paint one session's
    // work into another.
    if (attachAbortRef.current) {
      try { attachAbortRef.current.abort(); } catch (e) { /* already done */ }
    }
    attachGenerationRef.current += 1;
    const attachId = { session : sessionId, generation : attachGenerationRef.current };
    const isCurrent = () => attachInFlightRef.current?.session === attachId.session
      && attachInFlightRef.current?.generation === attachId.generation;
    attachInFlightRef.current = attachId;
    const controller = typeof AbortController === "function" ? new AbortController() : null;
    attachAbortRef.current = controller;
    let nothingHere = false;
    let completed = false;
    let attachFailed = false;
    // The replay debt this attach can settle: what was owed when it started. An overflow
    // during it is not covered (its events may predate the cursor this attach continued
    // from... or not — the next replay settles it).
    const debtAtStart = replayDebtRef.current.owed;
    try {
      const jobs = Object.entries(replayCursorRef.current).map(([job_id, last_seq]) => ({ job_id, last_seq }));
      // The socket's word has lapsed (a reconnect drops its subscriptions): ask again.
      if (!socketLive() && subscribeProgressRef.current) subscribeProgressRef.current();
      const response = await streamAPI.post("/answer/review", {
        session_id: sessionId,
        jobs,
        protocol: STREAM_PROTOCOL,
        // With the socket KNOWN to be delivering live progress, only the replay (and a
        // finished review's answer) is needed here; the api returns as soon as the store is
        // drained. Otherwise it tails.
        tail: !socketLive(),
      }, controller ? { signal: controller.signal } : {});
      if (!response || !response.ok || !response.body) {
        // 404 is the api's "nothing here" — the normal case. Anything else (a 5xx, no
        // body) is a failure to attach: nothing was learned, nothing replayed.
        if (response && response.status === 404) nothingHere = true;
        else attachFailed = true;
        return;
      }

      const reader = response.body.getReader();
      const parser = createFrameParser();
      const decoder = new TextDecoder();
      // The review whose composed answer is streaming right now — between its 'answer'
      // frames. It gets its OWN buffer and message: a question the office asks while this
      // stream is running writes to the answer path's buffer, and the two must never mix.
      let answering = null;
      let answerChunks = [];
      // The one review announced here that is finished but not yet answered: an older api
      // streams its answer bare, without 'answer' frames.
      let legacyAnswerTarget = null;
      let legacyAnswering = false;
      let reviewsAnnounced = 0;

      /** The message a review's composed answer goes into (the same one every time). */
      const answerIndex = (list, { job_id: answerJobId, qa_id: answerQaId }) => {
        // The answer takes the PLACEHOLDER'S PLACE — the entry of the turn that asked,
        // found by id — so it stays with its question even if the office asked something
        // else while the review ran, and two reviews in one chat cannot swap answers.
        const index = list.findIndex((m) => m.type === "server" && m.reviewAnswerForJob === answerJobId);
        return index !== -1 ? index : findTurnIndex(list, { qaId: answerQaId, jobId: answerJobId, byPlaceholder: true });
      };

      // The text between a review's 'answer' brackets, as it streams. The turn stays
      // pending — and the review unanswered — until the END bracket says the text was
      // COMMITTED (persisted as the answer): a message about a failed write-up streams the
      // same way, uncommitted, and the client asks again rather than take it for the answer.
      const applyReviewAnswer = (chunk) => {
        const { job_id: answerJobId, qa_id: answerQaId } = answering;
        answerChunks.push(fixPartialHtml(chunk.replace(/(```html|```)/g, "")));
        const content = answerChunks.join("");
        // The target is captured HERE: the updater runs later, by when `answering` may
        // already be the next review's — or null.
        const target = { job_id: answerJobId, qa_id: answerQaId };
        setMessages((prevMessages) => {
          const list = [...prevMessages];
          const index = answerIndex(list, target);
          const base = index !== -1 ? list[index] : { type: "server" };
          const answer = {
            ...base,
            type : "server",
            content,
            qa_id : answerQaId || base.qa_id || null,
            reviewAnswerFor : attachId.generation,
            reviewAnswerForJob : answerJobId,
            isReviewPlaceholder : false,
            activity : { ...(base.activity || emptyActivity()), reviewJobId: base.activity?.reviewJobId || answerJobId },
          };
          if (index !== -1) list[index] = answer;
          else list.push(answer);
          return list;
        });
      };

      const settleReviewAnswer = (target, { committed }) => {
        const { job_id: answerJobId } = target;
        if (committed && answerJobId) jobAnsweredRef.current[answerJobId] = true;
        setMessages((prevMessages) => {
          const list = [...prevMessages];
          const index = answerIndex(list, target);
          if (index === -1) return prevMessages;
          const m = list[index];
          const activity = { ...(m.activity || emptyActivity()), reviewJobId: m.activity?.reviewJobId || answerJobId };
          list[index] = committed
            ? {
              ...m,
              pending: false,
              isReviewPlaceholder: false,
              activity: settleSteps({ ...activity, reviewAnswered: true, status: "complete", endedAt: activity.endedAt || Date.now() }),
            }
            : {
              // Not the answer: the text says what happened, the report keeps working, and
              // the re-attach that follows (with backoff) asks for the answer again.
              ...m,
              pending: true,
              activity: { ...activity, reviewAnswered: false, status: TERMINAL_JOB_STATUSES.includes(activity.status) ? activity.status : "running" },
            };
          return list;
        });
      };

      const handleEvent = (event) => {
        if (!isCurrent()) return; // superseded by a newer attach
        if (event.kind === "frame") {
          const { frame } = event;
          if (frame.type === "answer") {
            if (frame.content?.phase === "start") {
              answering = { job_id: frame.content.job_id || null, qa_id: frame.content.qa_id || null };
              answerChunks = [];
              legacyAnswering = false;
            } else if (answering) {
              const hadText = answerChunks.some((c) => c.trim());
              // An older api says nothing about commitment: its bracketed text was the answer.
              const committed = frame.content?.committed === undefined ? hadText : !!frame.content.committed;
              if (committed && !hadText && answering.qa_id) {
                // Another tab composed it: the answer is in the entry, not on this stream.
                refreshQaEntry(answering.qa_id, sessionId);
              } else {
                settleReviewAnswer(answering, { committed });
              }
              answering = null;
            }
            return;
          }
          if (frame.type === "job_created" && (frame.content?.kind || "review") === "review") {
            reviewsAnnounced += 1;
            const finishedUnanswered = TERMINAL_JOB_STATUSES.includes(frame.content?.status) && !frame.content?.answered;
            legacyAnswerTarget = reviewsAnnounced === 1 && finishedUnanswered
              ? { job_id: frame.content.job_id || null, qa_id: frame.content.qa_id || null }
              : null;
          }
          applyStructuredFrame(frame, { job_id: frame.content?.job_id || null, source: "attach", sessionId: attachId.session });
          return;
        }
        if (!answering) {
          if (!legacyAnswerTarget || !event.text.trim()) return; // text with nowhere to go
          answering = legacyAnswerTarget;
          answerChunks = [];
          legacyAnswering = true;
        }
        if (!event.text.trim() && !answerChunks.length) return;
        applyReviewAnswer(event.text);
      };

      for (;;) {
        if (!isCurrent()) break; // a newer attach took over
        const { done, value } = await reader.read();
        if (done) {
          [...parser.push(decoder.decode()), ...parser.flush()].forEach(handleEvent);
          if (answering && isCurrent()) {
            // The stream ended inside an answer: an older api's bare answer is complete
            // here; a bracketed one that never reached its end bracket was cut off and is
            // asked for again.
            settleReviewAnswer(answering, { committed: legacyAnswering && answerChunks.some((c) => c.trim()) });
            answering = null;
          }
          break;
        }
        parser.push(decoder.decode(value, { stream: true })).forEach(handleEvent);
      }
      if (isCurrent()) completed = true;
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Error re-attaching to the chat's running work:", error);
        attachFailed = true;
      }
    } finally {
      if (isCurrent()) {
        attachInFlightRef.current = null;
        if (attachAbortRef.current === controller) attachAbortRef.current = null;
        // Come back when there is a reason to.
        const schedule = (delay) => {
          const generation = attachGenerationRef.current;
          reattachTimerRef.current = setTimeout(() => {
            reattachTimerRef.current = null;
            if (attachGenerationRef.current !== generation) return;
            if (activeSessionIdRef.current !== sessionId) return;
            attachSession(sessionId);
          }, delay);
        };
        // The replay owed when this attach started has been done (or there is nothing to replay).
        if (completed || nothingHere) replayDebtRef.current.settled = Math.max(replayDebtRef.current.settled, debtAtStart);
        if (attachFailed) {
          // The api could not serve the attach (a 5xx, the network): nothing was learned and
          // nothing replayed — a replay owed stays owed. Try again with backoff, a bounded
          // number of times in a row.
          const failures = attachFailuresRef.current + 1;
          attachFailuresRef.current = failures;
          if (failures <= retryPolicy.attempts) schedule(Math.min(retryPolicy.max_ms, retryPolicy.attach_fail_base_ms * 2 ** (failures - 1)));
        } else if (nothingHere) {
          attachFailuresRef.current = 0;
          // Nothing running — so any placeholder still on screen has its answer server-side
          // (or never will: no job exists for it any more, so it is left as it is).
          for (const m of messagesRef.current || []) {
            if (m.type === "server" && m.qa_id && showsPlaceholder(m)) refreshQaEntry(m.qa_id, sessionId, { keepPendingIfPlaceholder: false });
          }
        } else {
          attachFailuresRef.current = 0;
          // Something is still running (the api ends a tail after a while, a connection can
          // drop — with the socket delivering live progress this is only a safety net), or a
          // finished job's answer is not on screen (another tab may be composing it, a
          // composition or a chat write failed and the api retries on the next attach) —
          // that one with backoff, and not forever.
          const stillRunning = Object.values(jobStatusRef.current).some((s) => !TERMINAL_JOB_STATUSES.includes(s));
          // Finished but its answer NOT ON SCREEN — a review whose answer is not composed
          // yet, a question whose recorded answer has not reached its entry, or one whose
          // entry could not be fetched: asked for again. What counts is what the office
          // sees, not what the server says it has persisted.
          const notShown = Object.entries(jobStatusRef.current)
            .filter(([id, s]) => TERMINAL_JOB_STATUSES.includes(s) && !answerDisplayed(id))
            .map(([id]) => id);
          const tries = answerRetryRef.current.attempts;
          for (const id of Object.keys(tries)) if (!notShown.includes(id)) delete tries[id]; // on screen now
          if (stillRunning) {
            schedule(socketLive() ? 30000 : 1500);
          } else {
            const eligible = notShown.filter((id) => (tries[id] || 0) < retryPolicy.attempts);
            if (eligible.length) {
              const attempt = Math.min(...eligible.map((id) => tries[id] || 0)) + 1;
              for (const id of eligible) tries[id] = (tries[id] || 0) + 1;
              schedule(Math.min(retryPolicy.max_ms, retryPolicy.base_ms * 2 ** (attempt - 1)));
            }
          }
        }
      }
    }
  };

  attachSessionRef.current = attachSession;

  /**
   * Live progress over the app's realtime socket (SS-4110): the api pushes a turn's steps
   * as it records them, worker pushes each batch of a call review — the same frames a
   * re-attach replays, so they go through the same code. A frame of the question this
   * chat is streaming right now is dropped (the stream already carries it); a review's
   * terminal frame triggers the re-attach that composes its answer.
   */
  const onRealtimeProgress = (payload) => {
    const data = payload?.data || payload;
    const frame = data?.frame;
    if (!frame || !frame.type) return;
    const sessionId = data.session_id ? String(data.session_id) : null;
    if (!sessionId || sessionId !== String(activeSessionIdRef.current || "")) return;
    if (frame.content?.request_id && frame.content.request_id === currentRequestIdRef.current) return;
    // A frame arriving proves the socket is delivering: keep trusting it for a while.
    socketLiveUntilRef.current = Date.now() + SOCKET_LEASE_MS;
    applyStructuredFrame(frame, { job_id: frame.content?.job_id || null, source: "socket", sessionId });
    if (frame.type === "progress" && (frame.content?.kind || "review") === "review" && reviewFinished(frame.content)) {
      // After the frame has been rendered (so the re-attach continues from its seq), and
      // after worker has committed the job's status behind its terminal nudge.
      setTimeout(() => {
        if (activeSessionIdRef.current === sessionId) attachSession(sessionId);
      }, 1500);
    }
  };

  useEffect(() => {
    const client = realtime?.current;
    const uid = typeof getUid === "function" ? getUid() : null;
    const bid = typeof getBid === "function" ? getBid() : null;
    if (!client || typeof client.subscribeToTopic !== "function" || !uid || !bid) {
      subscribeProgressRef.current = null;
      return undefined;
    }
    const topic = `${PROGRESS_TOPIC_BASE}/${bid}/${uid}`;
    const handler = (...args) => onRealtimeProgress(args[0]);
    // Live only once the server has acknowledged the subscription — a rejected or lost
    // subscription must leave the client tailing over HTTP.
    const subscribe = () => {
      try {
        // A renewal must reach the server: the client library acknowledges a topic it is
        // already holding locally, without a round trip, so the subscription is dropped
        // first — and its listener with it, or every renewal would add one more.
        if (typeof client.unsubscribeFromTopic === "function") client.unsubscribeFromTopic(topic, bid, handler);
        client.subscribeToTopic(topic, bid, handler, undefined, () => {
          socketLiveUntilRef.current = Date.now() + SOCKET_LEASE_MS;
        });
      } catch (error) {
        console.error("Could not subscribe to live progress:", error);
      }
    };
    subscribeProgressRef.current = subscribe;
    subscribe();
    return () => {
      subscribeProgressRef.current = null;
      socketLiveUntilRef.current = 0;
      if (typeof client.unsubscribeFromTopic === "function") client.unsubscribeFromTopic(topic, bid, handler);
    };
  }, [realtimeInitialized, realtime, selectedBusiness?.bid]);

  /**
   * One question's turn: its bubbles, the streamed answer, and how it ends. `attached`
   * (an upload, SS-4267) shows nothing until the api has ACCEPTED the file: a refused one
   * leaves the chat exactly as it was.
   */
  const runTurn = async ({ endpoint, body, userMessage, attached = null }) => {
    const requestId = newRequestId();
    currentRequestIdRef.current = requestId;
    let shown = false;
    const showTurn = () => {
      shown = true;
      // An upload is shown only once the api accepted it, so the chat's own recovery may
      // already have fetched its entry (question + placeholder answer). Its stored file name
      // is unique: that recovered copy gives way to this live turn, never shown twice.
      const storedNames = (userMessage.q_buffers || []).map((b) => b.fileName).filter(Boolean);
      if (storedNames.length) messagesRef.current = withoutRecoveredTurn(messagesRef.current || [], storedNames);
      setMessages((prevMessages) => [
        ...withoutRecoveredTurn(prevMessages, storedNames),
        userMessage,
      ]);

      currentServerChunks.current = [];
      // The turn's own message, keyed so streamed text finds it wherever it sits, with its
      // report already running.
      setMessages((prevMessages) => [
        ...prevMessages,
        { type: "loading", content: null, turnKey: requestId, activity: { ...emptyActivity(), status: "running", startedAt: Date.now() } },
      ]);
    };
    if (!attached) showTurn();

    try {
      const newSession = messages.length === 0;

      setDisableSendButton(true);
      setIsStreaming(true);

      // Create AbortController for this request
      const controller = new AbortController();
      abortControllerRef.current = controller;
      abortReasonRef.current = null;

      const response = await streamAPI.post(endpoint, {
        ...body,
        request_id: requestId,
        protocol: STREAM_PROTOCOL,
      }, { signal: controller.signal });

      if (attached) {
        if (!response.ok) {
          await attached.onRefused(response);
          return;
        }
        attached.onAccepted();
        showTurn();
      }

      const reader = response.body.getReader();
      reviewJobIdRef.current = null;
      launchTurnJobRef.current = null;
      await processReadableStream(reader, chatSessionId);
      // The stream ended: the turn it named is over, and its answer is on this screen (a
      // review it launched is followed separately, below).
      const turnJob = launchTurnJobRef.current;
      if (turnJob) {
        if (!TERMINAL_JOB_STATUSES.includes(jobStatusRef.current[turnJob])) jobStatusRef.current[turnJob] = "complete";
        jobAnsweredRef.current[turnJob] = true;
      }
      // A review launched by this turn: follow its progress on the SAME path a returning
      // client uses — in the background, so the office can keep asking meanwhile.
      if (reviewJobIdRef.current) {
        attachSession(chatSessionId);
      }
      if (newSession) {
        const sessionIndex = `${chatContext.type}${
          selectedBusiness && selectedBusiness.bid
        }` + (chatContext.for ? `:${chatContext.for}` : "");
        setNewSessionCreated(!newSessionCreated);
        if (newButUnusedSessions.current[sessionIndex]) {
          newButUnusedSessions.current[sessionIndex] = undefined;
        }
      }
    } catch (error) {
      if (!shown) {
        // An upload whose answer never came (SS-4267): nothing to paint over — but the server
        // may have taken it before the answer was lost (a Stop, a dropped connection). Leaving
        // the chat is not a question about it: that chat shows the turn when it is back.
        if (attached && !(error.name === 'AbortError' && abortReasonRef.current === "navigation")) {
          await attached.onUnknown(error);
        }
      } else if (error.name === 'AbortError') {
        if (abortReasonRef.current === "navigation") {
          // The office left the chat: the question keeps running on the server and its
          // answer lands in the chat; nothing to paint here.
          console.log("Stopped reading the answer: the chat was left");
        } else {
          console.log("Request aborted by user");
          setMessages((prevMessages) => {
            const updatedMessages = [...prevMessages];
            let index = findTurnIndex(updatedMessages, { turnKey: requestId });
            if (index === -1) index = updatedMessages.length - 1;
            if (index >= 0) {
              const m = updatedMessages[index];
              updatedMessages[index] = {
                ...m,
                type: "server",
                content: "Request cancelled.",
                pending: false,
                activity: m.activity ? settleSteps({ ...m.activity, status: "cancelled", endedAt: Date.now() }) : m.activity,
              };
            }
            return updatedMessages;
          });
        }
      } else {
        console.error("Error making API call:", error);
        setMessages((prevMessages) => {
          const updatedMessages = [...prevMessages];
          let index = findTurnIndex(updatedMessages, { turnKey: requestId });
          if (index === -1) index = updatedMessages.length - 1;
          if (index >= 0) {
            const m = updatedMessages[index];
            updatedMessages[index] = {
              ...m,
              type: "server",
              content: "Error occurred while fetching data.",
              pending: false,
              activity: m.activity ? settleSteps({ ...m.activity, status: "failed", endedAt: Date.now() }) : m.activity,
            };
          }
          return updatedMessages;
        });
      }
    } finally {
      setDisableSendButton(false);
      setIsStreaming(false);
      setLongProcessing(false);
      abortControllerRef.current = null;
      if (currentRequestIdRef.current === requestId) currentRequestIdRef.current = null;
    }
  };

  const handleStopRequest = () => {
    // An attached file still uploading (SS-4267): Stop cancels the upload, nothing was asked yet.
    if (uploadAbortRef.current) {
      uploadAbortRef.current.abort();
      return;
    }
    if (abortControllerRef.current) {
      abortReasonRef.current = "user";
      abortControllerRef.current.abort();
      // The question keeps running on the server unless told otherwise (a closed
      // connection is not a Stop any more): this is the telling, for THIS question.
      cqAPI.post("/answer/stop", {
        session_id: activeSessionIdRef.current || chatSessionId,
        ...(currentRequestIdRef.current ? { request_id: currentRequestIdRef.current } : {}),
      }).catch(() => {});
    }
    if (longProcessingTimerRef.current) {
      clearTimeout(longProcessingTimerRef.current);
      longProcessingTimerRef.current = null;
    }
    setLongProcessing(false);
  };

  /** The Stop button on a review's step of the report: stops that review, nothing else. */
  const handleStopReview = (jobId) => {
    if (!jobId) return;
    cqAPI.post("/answer/stop", { session_id: activeSessionIdRef.current || chatSessionId, job_id: jobId }).catch(() => {});
  };

  useEffect(() => {
    const selectedPath = location.pathname.split("/").filter((x) => !!x);
    // Any route that no longer names the attached session — including leaving the history
    // route altogether for a fresh chat — ends that attach, so its progress and its answer
    // cannot land in whatever is on screen now.
    const routeSession = selectedPath[0] === "history" ? selectedPath[1] : null;
    if (attachInFlightRef.current && attachInFlightRef.current.session !== routeSession) {
      invalidateReviewAttach();
    }
    if (selectedPath[0] === "history" && selectedPath[1] !== chatSessionId) {
      // Another session is being shown: stop reading the one that was streaming (the
      // question itself keeps running on the server) and stop applying its attach.
      abortForNavigation();
      invalidateReviewAttach();
      activeSessionIdRef.current = selectedPath[1];
      setChatSessionId(selectedPath[1]);
      getSession(selectedPath[1])
        .then((x) => x.data[0])
        .then((session) => {
          if (!session) return;
          // A load that resolves after the office has moved on must not repaint the chat
          // that is on screen now (and must not hand a stale message list to the attach
          // below, which is what this feature writes its progress into).
          const pathNow = (currentPathnameRef.current || "").split("/").filter((x) => !!x);
          if (pathNow[0] !== "history" || pathNow[1] !== selectedPath[1]) return;
          setChatContext({
            type: session.type,
            name: session.patient_name,
            history: true,
            ...(session.type_id ? { [session.type_id]: session.type_id_value } : {}),
          });

          const messages = session.qa.map((msg) => {
            // The entry's id ties a review's progress and a late answer to THIS turn (SS-4110).
            const qaId = msg._id ? String(msg._id) : null;
            return [
              { type: "user", content: msg.q_text, q_buffers: msg.q_buffers ?? [], qa_id: qaId },
              { type: "server", content: msg.r_text, r_buffers: msg.r_buffers ?? [], qa_id: qaId },
            ];
          });
          const flat = messages.flat();
          // Eagerly too: the attach below decides "is this turn on screen?" before React renders.
          messagesRef.current = flat;
          setMessages(flat);
          // Anything of this chat still running, or finished while nobody was here: replay
          // its progress and pick up the answer (SS-4110). Only the office optimizer has
          // such work; other chats have nothing to ask for.
          if (session.type === "practice_optimizer") attachSession(selectedPath[1]);
        });
    }
  }, [location.pathname]);

  // ON MOUNT ONLY, and only for the one case the history effect above skips: the route
  // already names the session this component was handed, so it never loads it and never
  // asks whether it has anything running. Every other navigation goes through that effect,
  // which attaches AFTER the history is on screen — attaching before it would have its
  // frames overwritten by the history load.
  const mountAttachedRef = useRef(false);
  useEffect(() => {
    if (mountAttachedRef.current) return;
    mountAttachedRef.current = true;
    if (chatContext?.type !== "practice_optimizer" || !chatSessionId) return;
    const selectedPath = location.pathname.split("/").filter((x) => !!x);
    const routeSession = selectedPath[0] === "history" ? selectedPath[1] : null;
    // Strictly the history route for THIS session: a fresh chat inherits nothing.
    if (routeSession !== chatSessionId) return;
    attachSession(chatSessionId);
  }, []);

  // Leaving the chat for another page: stop READING, never the question (SS-4110).
  useEffect(() => () => {
    abortForNavigation();
    invalidateReviewAttach();
  }, []);

  async function fetchChatHistory() {
    if (!chatContext.type) {
      setChatHistory([]);
      setIsLoading(false); // Stop loading if there's no chat context type
      return;
    }
    setIsLoading(true); // Start loading
    try {
      const res = await cqAPI.get("/sessions", {
        count: 30,
        type: chatContext?.type,
        ...(chatContext.for ? { for: chatContext.for } : {})
      });
      if (res.type === 'ok') {
        setChatHistory(res.data);
      }
    } catch (err) {
      console.log('Error retrieving session history', err);
    } finally {
      setIsLoading(false); // Ensure loading ends on success or error
    }
  }

  useEffect(() => {
    fetchChatHistory(); // Call the function directly
  }, [chatContext]); // Depend on `chatContext` to trigger the effect when it changes

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const isSessionCompleted = async (runNumber = 0) => {
    const s = await getSession(chatSessionId).then((x) => x.data[0]);
    if (s.conversation_label) return true;
    if (runNumber > 10) return false;
    await wait(2000);
    return isSessionCompleted(runNumber + 1);
  };

  useEffect(() => {
    if (!chatSessionId || !newSessionCreated) return;
    isSessionCompleted().then((x) => {
      if (!x) return;
      cqAPI
        .get("/sessions", {
          count: 30,
          type: chatContext?.type,
          ...(chatContext.for ? {for: chatContext.for} : {})
        })
        .then((res) => {
          if (res.type === 'ok') {
            setChatHistory(res.data);
          }
          const newSearchParams = new URLSearchParams(window.location.search);
          navigate(`/history/${chatSessionId}?${newSearchParams.toString()}`);
        });
    });
  }, [newSessionCreated]);

  // ── Attaching a file (SS-4267) ─────────────────────────────────────────
  // Office Optimizer, one practice selected (a multi-location chat — no practice in the
  // address — only answers questions and takes no files).
  const canAttach = chatContext?.type === "practice_optimizer" && !!getBid() && !!chatSessionId;

  const attachError = (description) => toast({
    title: "This file can't be attached.",
    description,
    status: "error",
    duration: 9000,
    isClosable: true,
  });

  // A file belongs to the chat it was attached in: another chat starts without it.
  useEffect(() => {
    attachCheckRef.current += 1;
    setAttachment(null);
    setAttachmentChecking(false);
    // an upload still running belongs to the chat it was sent from (it goes there): this chat
    // shows no upload of its own, and its Stop cannot cancel that one
    uploadAbortRef.current = null;
    setUploading(false);
  }, [chatSessionId, chatContext?.type, chatContext?.for]);

  // The chat on screen, by kind and practice (its session may lag behind a switch), for an
  // upload that settles after the office moved.
  const chatKeyRef = useRef(null);
  chatKeyRef.current = `${chatContext?.type}${selectedBusiness && selectedBusiness.bid}`
    + (chatContext?.for ? `:${chatContext.for}` : "");
  // Whether this chat is still mounted: switching practices remounts it, and an upload that
  // finishes after that belongs to a chat no longer on screen.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      attachCheckRef.current += 1;
    };
  }, []);

  /** A picked file: checked here first (type, 10 MB, 5,000 rows) — a refused one is never uploaded. */
  const handleAttach = async (file) => {
    const check = ++attachCheckRef.current;
    // the previous file is replaced by this one, whatever its check says
    setAttachment(null);
    setAttachmentChecking(true);
    let problem;
    try {
      problem = await checkAttachment(file);
    } catch (error) {
      problem = "The file could not be read.";
    }
    if (check !== attachCheckRef.current) return; // a newer pick, or another chat
    setAttachmentChecking(false);
    if (problem) {
      attachError(problem);
      return;
    }
    setAttachment({ file, name: sanitizeFileName(file.name) });
  };

  /** A refused send puts its text back in the box — unless something new was typed meanwhile. */
  const restoreDraft = (text) => setQuestionText((current) => (current ? current : (text || "")));

  /**
   * Whether the chat holds this upload (its stored name is unique): true or false, or null when
   * the chat could not be read. Asked when the answer to /attachAnswerFile was lost, a few
   * times over a few seconds: the api may still be checking the file when it is first asked.
   */
  const acceptedOnServer = async (sessionId, fileName) => {
    let known = false;
    for (const delay of UPLOAD_CHECK_DELAYS_MS) {
      // eslint-disable-next-line no-await-in-loop
      if (delay) await new Promise((resolve) => setTimeout(resolve, delay));
      try {
        // eslint-disable-next-line no-await-in-loop
        const data = await getSession(sessionId);
        if (data?.type === "ok") {
          known = true;
          const entries = data?.data?.[0]?.qa ?? [];
          if (entries.some((e) => (e.q_buffers || []).some((b) => b?.fileName === fileName))) return true;
        }
      } catch (e) {
        // could not read the chat this time
      }
    }
    return known ? false : null;
  };

  /** A chat that now holds a turn is not a new one: coming back must not reuse it as empty. */
  const markChatUsed = (sessionIndex, sessionId) => {
    if (newButUnusedSessions.current[sessionIndex]?.session_id === sessionId) {
      newButUnusedSessions.current[sessionIndex] = undefined;
      setNewSessionCreated((v) => !v);
    }
  };

  /**
   * An accepted upload whose live answer was lost: its turn is shown, and the chat follows it —
   * only while the office is still in that chat (sessionIndex: its kind and practice), checked
   * again after the chat is read, so another chat's reader is never replaced.
   */
  const showAcceptedUpload = async (sessionId, sessionIndex) => {
    markChatUsed(sessionIndex, sessionId);
    const here = () => mountedRef.current && chatSessionIdRef.current === sessionId && chatKeyRef.current === sessionIndex;
    if (!here()) return;
    let entries = null;
    try {
      const data = await getSession(sessionId);
      entries = data?.data?.[0]?.qa ?? [];
    } catch (e) {
      // the chat's own recovery shows it when it next reads the chat
    }
    if (!here()) return;
    if (entries) appendMissingEntries(entries);
    attachSession(sessionId);
  };

  /** Upload the attached file as private/<uuid>-<name>, then ask the question with it. */
  const sendWithAttachment = async (text, current) => {
    if (!chatSessionId || !current?.file) return;
    // A file sent before whose answer was lost keeps its stored name: the api recognises it as
    // the same upload (409 already_attached) instead of asking — and billing — a second time.
    const fileName = current.uploadedAs || storedFileName(current.file.name);
    const kind = extensionOf(fileName);
    // This send's pick of this chat. If the office moves to another chat (or picks again)
    // before it settles, its outcome must not clear THAT attachment or fill THAT box.
    const generation = attachCheckRef.current;
    const stillHere = () => attachCheckRef.current === generation;
    // the practice and chat this send belongs to, as they are now
    const sendBid = getBid();
    const sessionIndex = `${chatContext.type}${selectedBusiness && selectedBusiness.bid}`
      + (chatContext.for ? `:${chatContext.for}` : "");
    const refused = (text_, message) => {
      if (!stillHere()) return;
      setAttachment(null);
      restoreDraft(text_);
      attachError(message);
    };
    setDisableSendButton(true);
    // the chip says the file is on its way until the api has taken it (or refused it)
    setAttachment((a) => (a && a.file === current.file ? { ...a, sending: true } : a));
    // already uploaded under this name (a retry): straight to asking
    if (!current.uploadedAs) {
      // The upload shows on the file's chip, and Stop cancels it; it is bounded, so a stalled
      // connection gives the message box back instead of holding it.
      const uploadController = new AbortController();
      uploadAbortRef.current = uploadController;
      setUploading(true);
      let uploaded;
      try {
        const formData = new FormData();
        formData.append("file", current.file, encodeURIComponent(current.name));
        const params = new URLSearchParams({ acl: "private", name: `private/${fileName}` });
        // one attempt: a retried upload would be a second copy of the file
        uploaded = await cqAPI.post(`/upload?${params.toString()}`, formData, UPLOAD_TIMEOUT_MS,
          { attempts: 1, signal: uploadController.signal });
      } catch (error) {
        uploaded = { type: "error", msg: error?.message };
      } finally {
        // only while it is still THIS chat's upload: one left behind must not end another's
        if (uploadAbortRef.current === uploadController) {
          uploadAbortRef.current = null;
          setUploading(false);
        }
      }
      if (uploaded?.type !== "ok") {
        if (uploaded?.aborted || uploadController.signal.aborted) {
          // Stopped by the office: the file stays attached and the text goes back in the box.
          if (stillHere()) {
            setAttachment((a) => (a && a.sending ? { ...a, sending: false } : a));
            restoreDraft(text);
            setDisableSendButton(false);
          }
          return;
        }
        refused(text, uploaded?.msg || "The file could not be uploaded.");
        // only for the chat this send belongs to: another one may be sending its own file
        if (stillHere()) setDisableSendButton(false);
        return;
      }
      // the object exists now: a retry of this attachment reuses it
      setAttachment((a) => (a && a.file === current.file ? { ...a, uploadedAs: fileName } : a));
    }

    // the office is looking at the chat this send belongs to (again)
    const backInThatChat = () => mountedRef.current && chatKeyRef.current === sessionIndex
      && chatSessionIdRef.current === chatSessionId;
    /**
     * A left chat's upload whose answer never came: shown if the api took it, otherwise the
     * office is told — only ever in THAT chat, checked again after every wait.
     */
    const settleLeftUpload = async (error) => {
      if (!backInThatChat()) return;
      const accepted = await acceptedOnServer(chatSessionId, fileName);
      if (accepted) {
        await showAcceptedUpload(chatSessionId, sessionIndex);
        return;
      }
      if (!backInThatChat()) return;
      restoreDraft(text);
      attachError(accepted === null
        ? `${error?.message || "The file could not be attached."} If it shows up in this chat, it was sent.`
        : (error?.message || "The file could not be attached."));
    };
    // Painted live only when nothing moved while the file uploaded (no other chat, practice or
    // pick in between): a turn started after a switch could land in the wrong chat or overlap
    // a question asked since.
    if (!mountedRef.current || !stillHere()) {
      // The question still goes to the chat it was asked in, where its answer lands (like any
      // question left running), and the api keeps — or refuses and deletes — the file.
      try {
        const response = await streamAPI.post("/attachAnswerFile", {
          session_id: chatSessionId,
          uploadedFiles: [{ fileName, displayName: current.name }],
          text,
          request_id: newRequestId(),
          protocol: STREAM_PROTOCOL,
        }, { bid: sendBid }); // the practice may have been switched meanwhile
        if (response?.ok) {
          // that chat is no longer new: coming back must not reuse it as an empty one
          if (newButUnusedSessions.current[sessionIndex]?.session_id === chatSessionId) {
            newButUnusedSessions.current[sessionIndex] = undefined;
          }
          if (response.body && typeof response.body.cancel === "function") await response.body.cancel();
          // the office is back in that very chat: it sees the turn and follows it, the way a
          // returning client does
          if (backInThatChat()) await showAcceptedUpload(chatSessionId, sessionIndex);
        } else if (response?.status === 409) {
          // the chat already holds this file: it was taken on an earlier try
          if (backInThatChat()) await showAcceptedUpload(chatSessionId, sessionIndex);
        } else if (isUnknownOutcome(response)) {
          // a gateway/server error: the api may have taken it all the same
          await settleLeftUpload(new Error(`The server did not answer (${response?.status}).`));
        } else if (backInThatChat()) {
          // refused, and the office is looking at that chat again: it is told, as it would
          // have been had it stayed (the chat itself is unchanged)
          let msg = "The file could not be attached.";
          try {
            const data = await response.json();
            if (data?.msg) msg = data.msg;
          } catch (e) {
            // not JSON: keep the generic message
          }
          if (backInThatChat()) {
            restoreDraft(text);
            attachError(msg);
          }
        }
      } catch (error) {
        console.log("Could not send the file of a chat that was left:", error?.message);
        await settleLeftUpload(error);
      }
      return;
    }

    // No answer from the api (a Stop, a dropped connection, a gateway error): the chat says
    // whether it took the file.
    const onUnknown = async (error) => {
      const accepted = await acceptedOnServer(chatSessionId, fileName);
      if (accepted) {
        if (stillHere()) setAttachment(null);
        await showAcceptedUpload(chatSessionId, sessionIndex);
        return;
      }
      if (error?.name === "AbortError") {
        // a Stop before the api took it: the file stays attached, the text goes back
        if (stillHere()) {
          setAttachment((a) => (a && a.sending ? { ...a, sending: false } : a));
          restoreDraft(text);
        }
        return;
      }
      // not refused — it never arrived: the file stays attached, so Send tries again
      if (!stillHere()) return;
      setAttachment((a) => (a && a.sending ? { ...a, sending: false } : a));
      restoreDraft(text);
      attachError(accepted === null
        ? `${error?.message || "The file could not be attached."} If it shows up in the chat, it was sent.`
        : (error?.message || "The file could not be attached."));
    };

    await runTurn({
      endpoint: "/attachAnswerFile",
      body: { session_id: chatSessionId, uploadedFiles: [{ fileName, displayName: current.name }], text },
      // what the chat shows for it on reload too: the text (or "Uploaded <name>") and the file
      userMessage: {
        type: "user",
        content: text && text.trim() ? text : `Uploaded ${current.name}`,
        q_buffers: [{ fileName, type: `${kind}-file` }],
      },
      attached: {
        onAccepted: () => { if (stillHere()) setAttachment(null); },
        onRefused: async (response) => {
          if (isUnknownOutcome(response)) {
            await onUnknown(new Error(`The server did not answer (${response?.status}).`));
            return;
          }
          if (response.status === 409) {
            // the chat already holds this file (an earlier try was taken): show that turn
            if (stillHere()) setAttachment(null);
            await showAcceptedUpload(chatSessionId, sessionIndex);
            return;
          }
          let msg = "The file could not be attached.";
          try {
            const data = await response.json();
            if (data?.msg) msg = data.msg;
          } catch (e) {
            // not JSON: keep the generic message
          }
          // the chat is unchanged, and what the office typed is still in the box
          refused(text, msg);
        },
        // no answer from the api: the chat says whether it took the file
        onUnknown,
      },
    });
  };

  const handleUserInput = async (input) => {
    // A file attached with the paperclip (SS-4267) goes with this message — only in a chat
    // that takes files.
    if (attachment && canAttach) return sendWithAttachment(input, attachment);
    if (attachment) setAttachment(null);
    return runTurn({
      endpoint: "/answer",
      body: { text: input, session_id: chatSessionId, for: chatContext.for, type: chatContext.type },
      userMessage: { type: "user", content: input },
    });
  };

  const fetchCampaignsByIds = async (ids) => {
    if (!ids || ids.length === 0) return [];
    try {
      const response = await cqAPI.post("/campaigns/by-ids", { ids });
      if (response.type === "ok") {
        return response.data;
      }
      return [];
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      return [];
    }
  };

  const handleNextClick = async () => {
    if (campaignIds && campaignIds.length > 0) {
      const fetchedCampaigns = await fetchCampaignsByIds(campaignIds);
      setCampaigns(fetchedCampaigns);
      setShowCampaignSummary(true);
    }
  };

  const handleCampaignUpdate = (campaignId, newContent, type) => {
    setCampaigns((prevCampaigns) =>
      prevCampaigns.map((campaign) => {
        if (campaign._id !== campaignId) return campaign;
        const campaignType = type || campaign.type;
        if (campaignType === "phone" && typeof newContent === "object") {
          const updatedPhone = { ...campaign.template?.phone };
          Object.entries(newContent).forEach(([key, value]) => {
            updatedPhone[key] = { ...updatedPhone[key], text: value };
          });
          return {
            ...campaign,
            template: { ...campaign.template, phone: updatedPhone },
          };
        }
        if (campaignType === "sms") {
          return {
            ...campaign,
            template: {
              ...campaign.template,
              sms: { ...campaign.template?.sms, template: newContent },
            },
          };
        }
        if (campaignType === "email") {
          return {
            ...campaign,
            template: {
              ...campaign.template,
              email: { ...campaign.template?.email, email_content: newContent },
            },
          };
        }
        return campaign;
      })
    );
  };

  const handleCampaignBack = () => {
    setShowCampaignSummary(false);
  };

  const handleScheduleSuccess = () => {
    // Reset the campaign summary after successful scheduling
    setShowCampaignSummary(false);
    setCampaigns([]);
  };

  const handleSendTest = async (testData) => {
    const { processDate, campaigns: campaignsToTest } = testData;

    try {
      for (const campaign of campaignsToTest) {
        const response = await cqAPI.post("/campaigns/send?confirm=true", {
          campaign: {
            ...campaign,
            type: campaign.type,
            purpose: campaign.purpose || "campaign-manual",
          },
          process_date: processDate.toISOString(),
        });

        if (response.type === "ok") {
          toast({
            title: "Success",
            description: `Test for ${campaign.name || campaign.type} campaign sent successfully!`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        } else {
          toast({
            title: "Error",
            description: response.msg || "Failed to send test campaign",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to send test campaign",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Update campaign_ids when session data is retrieved
  useEffect(() => {
    if (chatSessionId) {
      getSession(chatSessionId).then((sessionData) => {
        const entries = sessionData?.data ?? [];
        const lastEntry = entries.length ? entries[entries.length - 1] : null;
        if (lastEntry?.campaign_ids && lastEntry.campaign_ids.length > 0) {
          setCampaignIds(lastEntry.campaign_ids);
        } else {
          setCampaignIds([]);
          setShowCampaignSummary(false);
          setCampaigns([]);
        }
      }).catch(err => console.error('Error getting session for campaign_ids:', err));
    }
  }, [chatSessionId, messages]);

  return (
    <div className="chat-container">
      <div className="chat-area">
        <header className="page-header">
          <h3>{chatContext.name || ""}</h3>
        </header>
        <MessageList
          fillQuestion={(question) => setQuestionText(question)}
          promptSuggestions={promptSuggestions}
          messages={messages}
          chatBoxRef={chatBoxRef}
          disableSendButton={disableSendButton}
          longProcessing={longProcessing}
          campaignIds={campaignIds}
          onNextClick={handleNextClick}
          showCampaignSummary={showCampaignSummary}
          campaigns={campaigns}
          onCampaignUpdate={handleCampaignUpdate}
          onSendTest={handleSendTest}
          onCampaignBack={handleCampaignBack}
          onScheduleSuccess={handleScheduleSuccess}
          onStopReview={handleStopReview}
        />
        <InputArea
          onSend={handleUserInput}
          onStop={handleStopRequest}
          questionText={questionText}
          setQuestionText={setQuestionText}
          disableSendButton={disableSendButton || attachmentChecking}
          isStreaming={isStreaming || uploading}
          canAttach={canAttach}
          attachment={attachment}
          onAttach={handleAttach}
          onRemoveAttachment={() => setAttachment(null)}
        />
        {isMobile && (
          <Collapse in={true}>
            <Box className="chat-history-mobile">
              <ChatHistory
                className="right-menu"
                chatSessionId={chatSessionId}
                chatHistory={chatHistory}
                isLoading={isLoading}
              />
            </Box>
          </Collapse>
        )}
      </div>
      {!isMobile && (
        <div className="right-menu">
          <ChatHistory
            chatSessionId={chatSessionId}
            chatHistory={chatHistory}
            isLoading={isLoading}
          />
        </div>
      )}
    </div>
  );
}

export default ChatComponent;
